import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api, { setSelectedBranches } from '../services/api';
import { getEngagementPortal } from '../engagementPortal';
import BranchSelect from '../components/BranchSelect';

/**
 * Who is signed in (from the server: /me) and which branches HQ is looking at.
 *
 * - Franchise desk: always its own branch; no picker.
 * - HQ / region admin: every branch, or a checked list, remembered per portal in this browser.
 *   One branch is sent as franchise_id. Several are sent as franchise_ids. Pages reload when it changes.
 */
const EngagementScopeContext = createContext({
  ready: false,
  me: null,
  sessionExpired: false,
  branches: [],
  selectedIds: [],
  setSelectedIds: () => {},
  selected: '',
  setSelected: () => {},
  isHQ: true,
  canManage: true,
  scopeType: 'legacy',
  branchName: () => '',
});

function storageKey() {
  return `naturals_engagement_branch_${getEngagementPortal()}`;
}

function readStored() {
  try {
    const raw = localStorage.getItem(storageKey()) || '';
    if (!raw) return [];
    if (raw.startsWith('[')) {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : [];
    }
    return [String(raw)];
  } catch {
    return [];
  }
}

function writeStored(ids) {
  try {
    if (!ids.length) localStorage.removeItem(storageKey());
    else localStorage.setItem(storageKey(), JSON.stringify(ids));
  } catch {
    /* private window: the choice lasts until reload */
  }
}

function uniqueIds(value) {
  const list = Array.isArray(value) ? value : value ? [value] : [];
  const next = [];
  list.forEach((id) => {
    const text = id == null ? '' : String(id);
    if (text && !next.includes(text)) next.push(text);
  });
  return next;
}

export function EngagementScopeProvider({ children }) {
  const [me, setMe] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [ready, setReady] = useState(false);
  const [selectedIds, setSelectedIdsState] = useState(() => {
    const stored = readStored();
    setSelectedBranches(stored);
    return stored;
  });

  useEffect(() => {
    let alive = true;
    api
      .me()
      .then((data) => {
        if (!alive) return;
        setMe(data);
        const ids = (data?.branches || []).map((b) => String(b.franchise_id));
        setSelectedIdsState((current) => {
          // A franchise desk never picks; a stored choice must still be one of this user's branches.
          const kept = data?.scope_type === 'franchise' ? [] : current.filter((id) => ids.includes(id));
          if (kept.length === current.length && kept.every((id, index) => id === current[index])) return current;
          setSelectedBranches(kept);
          writeStored(kept);
          return kept;
        });
      })
      .catch((e) => {
        if (alive) {
          setMe(null);
          setSessionExpired(e?.status === 401);
        }
      })
      .finally(() => {
        if (alive) setReady(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  const setSelectedIds = useCallback((value) => {
    const next = uniqueIds(value);
    setSelectedBranches(next);
    setSelectedIdsState(next);
    writeStored(next);
  }, []);

  const setSelected = useCallback((value) => {
    setSelectedIds(value == null || value === '' ? [] : [value]);
  }, [setSelectedIds]);

  const value = useMemo(() => {
    const branches = me?.branches || [];
    const scopeType = me?.scope_type || 'legacy';
    const names = Object.fromEntries(branches.map((b) => [String(b.franchise_id), b.label || b.name]));
    const selected = selectedIds.length === 1 ? selectedIds[0] : '';
    return {
      ready,
      me,
      sessionExpired,
      branches,
      selectedIds,
      setSelectedIds,
      selected,
      setSelected,
      scopeType,
      isHQ: scopeType === 'organization' || scopeType === 'legacy',
      canManage: me ? Boolean(me.can_manage) : true,
      sees: (fid) => scopeType !== 'franchise' || String(fid) === String(me?.franchise_id),
      branchName: (fid) => (fid == null || fid === '' ? '' : names[String(fid)] || ''),
      // Show a Branch column when more than one branch is in view.
      multiBranch: scopeType !== 'franchise' && branches.length > 1 && selectedIds.length !== 1,
    };
  }, [me, ready, selectedIds, setSelected, setSelectedIds, sessionExpired]);

  return <EngagementScopeContext.Provider value={value}>{children}</EngagementScopeContext.Provider>;
}

export function useEngagementScope() {
  return useContext(EngagementScopeContext);
}

/** HQ / region picker: every branch, or the ones checked. Hidden for a franchise desk and single-branch orgs. */
export function BranchPicker() {
  const { branches, selectedIds, setSelectedIds, scopeType } = useEngagementScope();
  if (scopeType === 'franchise' || branches.length < 2) return null;
  const all = selectedIds.length === 0;
  return (
    <div className="de-row" style={{ gap: 8, alignItems: 'center' }}>
      <span className="de-small de-muted">Branch</span>
      <div style={{ minWidth: 280, maxWidth: 420, flex: '1 1 280px' }}>
        <BranchSelect multiple branches={branches} value={selectedIds} onChange={setSelectedIds} placeholder="All branches" />
      </div>
      <button
        type="button"
        className={`de-btn de-btn--sm ${all ? 'de-btn--primary' : 'de-btn--outline'}`}
        aria-pressed={all}
        onClick={() => setSelectedIds([])}
      >
        All
      </button>
    </div>
  );
}

/** One salon, or the organization default. Used where several salons cannot be edited together. */
export function SettingsBranchSelect({ value, onChange }) {
  const { branches, scopeType } = useEngagementScope();
  if (scopeType === 'franchise' || branches.length < 2) return null;
  return (
    <div className="de-row" style={{ gap: 8, alignItems: 'center', marginBottom: 12 }}>
      <span className="de-small de-muted">Settings for</span>
      <div style={{ minWidth: 280, maxWidth: 420, flex: '1 1 280px' }}>
        <BranchSelect
          branches={branches}
          value={value}
          onChange={onChange}
          emptyLabel="Organization default"
          placeholder="Organization default"
        />
      </div>
    </div>
  );
}

/** Shown when settings can be saved for one branch or the organization, and several branches are checked. */
export function ManyBranchesNotice() {
  return (
    <div className="de-callout">
      Several branches are selected. Pick one branch above to change that branch, or clear the branch filter to change the organization default.
    </div>
  );
}

/** Small "Velachery" tag for rows; "HQ queue" for rows with no branch yet. */
export function BranchTag({ franchiseId, name, queue }) {
  const { branchName } = useEngagementScope();
  if (queue || franchiseId == null) {
    return <span className="de-pill de-pill--warn de-pill--sm" title="No branch chosen yet: HQ handles it">HQ queue</span>;
  }
  const label = name || branchName(franchiseId);
  return label ? <span className="de-pill de-pill--muted de-pill--sm" title={label}>{label.split(' (')[0]}</span> : null;
}
